import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircularProgress, {
  circularProgressClasses,
} from "@mui/material/CircularProgress";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import i18n from "../../../../utils/i18n.js";
import classes from "./contactOfferIntro.module.scss";
import TestimonialCarousel from "./TestimonialCarousel.jsx";

const LOADING_STEPS = [
  i18n.t("searchflow.loadingstep1"),
  i18n.t("searchflow.loadingstep2"),
  i18n.t("searchflow.loadingstep3"),
  i18n.t("searchflow.loadingstep4"),
  i18n.t("searchflow.loadingstep5"),
];

/** Time each loading subtitle stays visible before advancing. */
const LOADING_STEP_MS = 2200;

const progressTrackSx = {
  color: "#dbeafe",
};

const progressBarSx = {
  color: "#1c32f3",
  position: "absolute",
  left: 0,
  [`& .${circularProgressClasses.circle}`]: {
    strokeLinecap: "round",
  },
};

const ContactOfferIntro = ({
  fetchAspTopProvidersByMatchScore,
  match_score_weights,
  onReveal,
}) => {
  const companyStore = useSelector((state) => state.company_store);
  const companyStoreRef = useRef(companyStore);
  companyStoreRef.current = companyStore;
  const [phase, setPhase] = useState("loading");
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const fetchDoneRef = useRef(false);
  const stepsDoneRef = useRef(false);

  const tryShowReady = useCallback(() => {
    if (fetchDoneRef.current && stepsDoneRef.current) {
      setPhase("ready");
    }
  }, []);

  useEffect(() => {
    if (phase !== "loading") {
      return undefined;
    }

    fetchDoneRef.current = false;
    stepsDoneRef.current = false;
    setLoadingStepIndex(0);

    const stepTimers = LOADING_STEPS.slice(1).map((_, index) =>
      window.setTimeout(() => {
        setLoadingStepIndex(index + 1);
      }, LOADING_STEP_MS * (index + 1)),
    );

    const stepsDoneTimer = window.setTimeout(() => {
      stepsDoneRef.current = true;
      tryShowReady();
    }, LOADING_STEP_MS * LOADING_STEPS.length);

    return () => {
      stepTimers.forEach((timerId) => window.clearTimeout(timerId));
      window.clearTimeout(stepsDoneTimer);
    };
  }, [phase, tryShowReady]);

  useEffect(() => {
    if (phase !== "loading") {
      return undefined;
    }

    if (typeof fetchAspTopProvidersByMatchScore !== "function") {
      fetchDoneRef.current = true;
      tryShowReady();
      return undefined;
    }

    let cancelled = false;

    Promise.resolve(
      fetchAspTopProvidersByMatchScore(companyStoreRef.current, match_score_weights),
    )
      .then(() => {
        if (!cancelled) {
          fetchDoneRef.current = true;
          tryShowReady();
        }
      })
      .catch(() => {
        if (!cancelled) {
          fetchDoneRef.current = true;
          tryShowReady();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchAspTopProvidersByMatchScore, match_score_weights, phase, tryShowReady]);

  const loadingSubtitle = LOADING_STEPS[loadingStepIndex] ?? LOADING_STEPS[0];

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            {phase === "loading" ? (
              <div className={classes.statusBlock}>
                <div className={classes.progressWrap} aria-hidden="true">
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={56}
                    thickness={5}
                    sx={progressTrackSx}
                  />
                  <CircularProgress
                    variant="indeterminate"
                    size={56}
                    thickness={5}
                    sx={progressBarSx}
                    disableShrink
                  />
                </div>
                <h1 className={`${classes.title} ${classes.textEnter}`}>
                  {i18n.t("searchflow.preparingofferheading")}
                </h1>
                <p
                  key={loadingStepIndex}
                  className={`${classes.subtitle} ${classes.textEnter}`}
                  aria-live="polite"
                >
                  {loadingSubtitle}
                </p>
              </div>
            ) : (
              <div className={classes.statusBlock}>
                <div className={`${classes.checkIcon} ${classes.textEnter}`} aria-hidden="true">
                  <CheckIcon />
                </div>
                <h1 className={`${classes.title} ${classes.textEnter}`}>
                  {i18n.t("searchflow.offerreadyheading")}
                </h1>
                <p className={`${classes.subtitle} ${classes.textEnterDelayed}`}>
                  {i18n.t("searchflow.offerreadysubtitle")}
                </p>
              </div>
            )}

            <div className={classes.testimonials}>
              <TestimonialCarousel />
            </div>
          </div>
        </div>
      </div>

      <div
        className={`${classes.footer} ${phase === "ready" ? classes.footerEnter : ""}`}
      >
        <div className={classes.footerInner}>
          <button
            type="button"
            className={`${classes.footerButton} ${
              phase === "ready" ? "" : classes.footerButtonHidden
            }`}
            onClick={phase === "ready" ? onReveal : undefined}
            aria-hidden={phase !== "ready"}
            tabIndex={phase === "ready" ? undefined : -1}
          >
            {i18n.t("searchflow.requestoffercta")}
            <ChevronRightIcon className={classes.footerIcon} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactOfferIntro;
